'use client'

import {
  ArtifactCanvasBody,
  useEvaluationAnalysis,
  useProfileById,
  getHrefWithThreadPreservation,
  formatEntityLocation,
} from '@yeshuman/sdk'
import type { JobAnalysisCoverage } from '../../../hooks/use-job-analysis-coverage'
import { buildJobWorkspacePersonPath } from '../../../lib/job-workspace-person-paths'
import { buildJobWorkspacePersonNavigationState } from '../../../lib/job-workspace-person-navigation'
import { CandidateHeader } from './candidate-header'
import { CandidateActionBar, type CandidateActionBarProps } from './candidate-action-bar'
import { CandidateVerdictSection } from './candidate-verdict-section'
import type { CandidatePanelSource } from './candidate-panel-types'
import { resolveCandidateCommitment } from './candidate-panel-types'

export type CandidatePanelProps = Omit<CandidateActionBarProps, 'source'> & {
  source: CandidatePanelSource
  jobId: string
  coverage?: JobAnalysisCoverage | null
  origin?: 'talent' | 'suggestions' | 'applicants' | 'analysis'
}

export function CandidatePanel({
  source,
  stages,
  jobId,
  origin = 'applicants',
  ...actionProps
}: CandidatePanelProps) {
  const commitment = resolveCandidateCommitment(source)
  const isProspect = commitment === 'prospect'
  const profileId =
    source.kind === 'applicant' ? source.applicant.profile.id : source.match.profile_id
  const { profile: fullProfile } = useProfileById(isProspect ? profileId : null)

  const evaluationId =
    source.kind === 'applicant'
      ? source.applicant.evaluation_id
      : source.match.evaluation_id

  const displayName =
    source.kind === 'applicant' ? source.applicant.profile.name : source.match.name

  const profileFirst = isProspect || !evaluationId

  const personMatchPath = buildJobWorkspacePersonPath(jobId, profileId, {
    evaluationId: evaluationId ?? undefined,
    ...(profileFirst ? { tab: 'profile' as const } : {}),
  })

  const reviewLabel = profileFirst ? 'View profile' : 'Open full review'

  const {
    payload: analysisPayload,
    isLoading: analysisLoading,
    isMissing: analysisMissing,
  } = useEvaluationAnalysis(evaluationId, {
    enabled: !!evaluationId && !isProspect,
  })

  const matchScore =
    source.kind === 'applicant' && typeof source.applicant.match_score === 'number'
      ? source.applicant.match_score
      : source.kind === 'prospect'
        ? source.match.match_percent / 100
        : null

  const isEstimate =
    matchScore != null &&
    (isProspect || (source.kind === 'applicant' && !source.applicant.evaluation_id))

  const prospectLocation =
    isProspect && fullProfile
      ? formatEntityLocation(fullProfile.city, fullProfile.country)
      : null

  return (
    <ArtifactCanvasBody className="flex min-h-0 flex-1 flex-col gap-6 space-y-0 overflow-y-auto">
      <CandidateHeader source={source} locationOverride={prospectLocation} />
      <CandidateActionBar source={source} stages={stages} {...actionProps} />

      <CandidateVerdictSection
        evaluationId={evaluationId}
        isProspect={isProspect}
        payload={analysisPayload}
        isLoading={analysisLoading}
        isMissing={analysisMissing}
        matchScore={matchScore}
        isEstimate={isEstimate}
        reviewTo={getHrefWithThreadPreservation(personMatchPath)}
        reviewState={buildJobWorkspacePersonNavigationState(jobId, displayName, { origin })}
        reviewLabel={reviewLabel}
      />
    </ArtifactCanvasBody>
  )
}
